import { Howl, Howler } from 'howler';
import { interval, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import type { PlayBackInfo } from './AudioPlayers.type';

class AudioEngine {
  /**
   * @var buffer The Howl entity
   * https://github.com/goldfire/howler.js#documentation
   */
  public buffer: Howl | undefined = undefined;

  public playlist: string[] = [];

  private index = 0;

  public onTrackEnd = new Subject<void>();

  private interval$ = interval(300);

  private destroy$: Subject<boolean> = new Subject<boolean>();

  public playBackInfo = new Subject<PlayBackInfo>();

  public get isBufferLoading(): boolean {
    return this.buffer !== undefined && this.buffer.state() === 'loading';
  }

  /**
   * Load the Howl buffer instance.
   */
  public load = (index: number): Promise<Howl | null> => {
    this.index = index;
    return new Promise((resolve, reject) => {
      this.buffer = new Howl({
        html5: true,
        src: [this.playlist[index]],
        onload: () => resolve(this.buffer || null),
        onloaderror: (err) => reject(err),
        onplayerror: (err) => reject(err),
        onend: () => this.onTrackEnd.next(),
        onplay: () => {
          this.destroy$.next(false);
          this.interval$
            .pipe(
              map(() => this.buffer?.seek()),
              takeUntil(this.destroy$),
            )
            .subscribe((currTime) => {
              if (!this.buffer || typeof currTime !== 'number') return;

              const remainSeconds = this.buffer.duration() - currTime;
              const percentage = (currTime / this.buffer.duration()) * 100;

              this.playBackInfo.next({
                position: percentage,
                time: this.secondsToTime(remainSeconds).mmss,
              });
            });
        },
        onpause: () => {
          this.destroy$.next(true);
        },
      });
    });
  };

  public seek(seekPercentage: number): void {
    if (!this.isBufferLoading && this.buffer) {
      const time = (seekPercentage / 100) * this.buffer.duration();
      this.buffer.seek(time);
    }
  }

  /**
   * Convert seconds to time format
   */
  private secondsToTime = (timeInSeconds: number) => {
    function pad(num: string | number, size: number): string {
      return `000${num}`.slice(size * -1);
    }
    const time = Number.parseFloat(timeInSeconds.toFixed(3));
    const hours = Math.floor(time / 60 / 60);
    const minutes = Math.floor(time / 60) % 60;
    const seconds = Math.floor(time - minutes * 60);
    const milliseconds = time.toString().slice(-3);

    return {
      hours: pad(hours, 2),
      minutes: pad(minutes, 2),
      seconds: pad(seconds, 2),
      millisecons: pad(milliseconds, 3),
      mmss: `- ${pad(minutes, 2)}:${pad(seconds, 2)}`,
    };
  };

  public next = (): Promise<number> => {
    return new Promise((resolve, reject) => {
      if (this.isBufferLoading) {
        reject(new Error('buffer is loading'));
      } else {
        this.destroy$.next(true);
        const newIndex = this.index + 1;
        const targetIndex = newIndex > this.playlist.length - 1 ? 0 : newIndex;
        this.unloadBuffer();
        this.load(targetIndex).then(
          () => {
            if (typeof this.buffer !== 'undefined') {
              this.playBackInfo.next({
                position: 0,
                time: this.secondsToTime(this.buffer.duration()).mmss,
              });
              resolve(newIndex);
            }
          },
          (err) => reject(err),
        );
      }
    });
  };

  public prev = (): Promise<number> => {
    return new Promise((resolve, reject) => {
      if (this.isBufferLoading) {
        reject(new Error('buffer is loading'));
      } else {
        this.destroy$.next(true);
        const newIndex = this.index - 1;
        const targetIndex = newIndex < this.playlist.length - 1 ? 0 : newIndex;
        this.unloadBuffer();
        this.load(targetIndex).then(
          () => {
            if (typeof this.buffer !== 'undefined') {
              this.playBackInfo.next({
                position: 0,
                time: this.secondsToTime(this.buffer.duration()).mmss,
              });
              resolve(newIndex);
            }
          },
          (err) => reject(err),
        );
      }
    });
  };

  public play = () => {
    if (this.buffer !== undefined) {
      this.buffer.play();
    }
  };

  public pause = () => {
    if (this.buffer !== undefined) {
      this.buffer.pause();
    }
  };

  public stop = () => {
    if (this.buffer !== undefined) {
      this.buffer.stop();
    }
  };

  public unloadBuffer(): void {
    if (this.buffer !== undefined) {
      this.buffer.unload();
      this.buffer = undefined;
      this.index = 0;
      this.destroy$.next(true);
    }
  }

  public reset(): void {
    this.unloadBuffer();
  }

  public destroy(): void {
    this.unloadBuffer();
    Howler.unload();
  }
}

export default new AudioEngine();
